import * as Antd from 'antd';

type ActionControlVariant = 'default' | 'primary' | 'text';
type ActionControlTone = 'primary' | 'danger';
type ActionControlSize = 'middle' | 'large';

type ActionControlStyleOptions = {
    variant?: ActionControlVariant;
    tone?: ActionControlTone;
    bordered?: boolean;
    iconOnly?: boolean;
    disabled?: boolean;
    size?: ActionControlSize;
};

type ActionControlToken = ReturnType<typeof Antd.theme.useToken>['token'];
type CssVarsStyle = React.CSSProperties & Record<`--${string}`, string | number | undefined>;

export const getActionControlStyleVars = (
    token: ActionControlToken,
    {
        variant = 'default',
        tone = 'primary',
        bordered = true,
        iconOnly = false,
        disabled = false,
        size = 'middle',
    }: ActionControlStyleOptions = {},
): React.CSSProperties => {
    const accentBaseColor = tone === 'danger' ? token.colorError : token.colorPrimary;
    const accentHoverColor = tone === 'danger' ? token.colorErrorHover : token.colorPrimaryHover;
    const accentActiveColor = tone === 'danger' ? token.colorErrorActive : token.colorPrimaryActive;

    const baseColor = disabled
        ? token.colorTextDisabled
        : variant === 'primary'
            ? token.colorTextLightSolid
            : variant === 'text'
                ? accentBaseColor
                : token.colorText;
    const hoverColor = disabled
        ? token.colorTextDisabled
        : variant === 'primary'
            ? token.colorTextLightSolid
            : variant === 'text'
                ? accentHoverColor
                : accentHoverColor;
    const activeColor = disabled
        ? token.colorTextDisabled
        : variant === 'primary'
            ? token.colorTextLightSolid
            : variant === 'text'
                ? accentActiveColor
                : accentActiveColor;

    const baseBorderColor = disabled
        ? token.colorBorder
        : variant === 'primary'
            ? accentBaseColor
            : bordered
                ? (variant === 'text' ? accentBaseColor : token.colorBorder)
                : 'transparent';
    const hoverBorderColor = disabled
        ? token.colorBorder
        : variant === 'primary'
            ? accentHoverColor
            : bordered
                ? accentHoverColor
                : 'transparent';
    const activeBorderColor = disabled
        ? token.colorBorder
        : variant === 'primary'
            ? accentActiveColor
            : bordered
                ? accentActiveColor
                : 'transparent';

    const baseBackgroundColor = disabled
        ? token.colorBgContainerDisabled
        : variant === 'primary'
            ? accentBaseColor
            : 'transparent';
    const hoverBackgroundColor = disabled
        ? token.colorBgContainerDisabled
        : variant === 'primary'
            ? accentHoverColor
            : token.colorBgTextHover;
    const activeBackgroundColor = disabled
        ? token.colorBgContainerDisabled
        : variant === 'primary'
            ? accentActiveColor
            : token.colorBgTextActive;

    const vars: CssVarsStyle = {
        '--action-control-color': baseColor,
        '--action-control-hover-color': hoverColor,
        '--action-control-active-color': activeColor,
        '--action-control-border-color': baseBorderColor,
        '--action-control-hover-border-color': hoverBorderColor,
        '--action-control-active-border-color': activeBorderColor,
        '--action-control-bg': baseBackgroundColor,
        '--action-control-hover-bg': hoverBackgroundColor,
        '--action-control-active-bg': activeBackgroundColor,
        '--action-control-focus-shadow': `0 0 0 ${token.lineWidthFocus}px ${token.colorPrimaryBorder}`,
        '--action-control-height': `${size === 'large' ? token.controlHeightLG : token.controlHeight}px`,
        '--action-control-padding': iconOnly ? '0' : `0 ${size === 'large' ? token.padding : token.paddingSM}px`,
        '--action-control-radius': `${token.borderRadius}px`,
        '--action-control-font-size': `${token.fontSize}px`,
        '--action-control-gap': `${token.marginXS}px`,
        '--action-control-font-weight': token.fontWeightStrong,
        '--action-control-transition': `all ${token.motionDurationMid}`,
    };

    return vars;
};
